import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../assets/fillopsLogo.png";
import { useUserAuth } from "../store/user";

const inputCls = (hasError) =>
  `w-full pl-10 pr-10 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
    hasError ? "border-red-400" : "border-gray-300 dark:border-gray-700"
  }`;

/**
 * /reset-password?token=… — set a new learner password.
 *
 *   POST /api/auth/reset-password  { token, password }
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { userResetPassword } = useUserAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // POST /api/auth/reset-password — { token, password }
  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) {
      setError("Missing reset token. Open the link from your reset email.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must contain at least one uppercase letter.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const result = await userResetPassword({ token, password });
    setLoading(false);
    if (!result.success) {
      setError(result.error || "Failed to reset password. The link may have expired.");
      return;
    }
    navigate("/login?reset=true", { replace: true });
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-white dark:bg-background px-3 py-6 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative w-full max-w-sm bg-white dark:bg-background border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg p-6 transition-colors duration-300"
      >
        <div className="flex justify-center mb-3">
          <img src={Logo} alt="App Logo" className="h-14 w-14 rounded-full shadow-md object-cover" />
        </div>

        <div className="text-center mb-5">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Reset password</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Choose a new password for your account.</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls(false)}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls(!!confirm && confirm !== password)}
            />
          </div>

          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            At least 8 characters with one uppercase letter.
          </p>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 ${
              loading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
            } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              "Reset password"
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="w-full inline-flex items-center justify-center gap-2 text-xs text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </button>
        </form>
      </motion.div>
    </section>
  );
};

export default ResetPassword;
